import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BsModalRef, ModalModule } from 'ngx-bootstrap/modal';
import { finalize, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs/internal/Subject';
import { FormService } from 'src/sb-modules/Frontend-form-field-module/common/services/form.service';
import { FormCommonDataService } from 'src/sb-modules/Frontend-form-field-module/common/services/form-common-data.service';
import { AddTicketModel } from 'src/sb-modules/Frontend-form-field-module/common/models/add-ticket-model.model';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { NotificationsService } from 'angular2-notifications';
import { FormComponent } from '../../components/form/form.component';
import { AttachmentService } from 'src/sb-tools/typescript/services/attachment.service';
import { MessageStorageService } from 'src/sb-modules/Frontend-form-field-module/common/services/message-storage.service';
import { FormFieldsValidationService } from 'src/sb-modules/Frontend-form-field-module/common/services/form-fields-validation.service';
import { environment } from '@environment';
import { ClientForm } from 'src/sb-modules/Frontend-form-field-module/common/models/client-form.model';
import { FormCategoryPreselectionService } from 'src/sb-modules/Frontend-form-field-module/common/services/form-category-preselection.service';
import { FormType } from '@sb-tools/typescript/enums/form-type.enum';
import { TicketStatus } from '@sb-tools/typescript/enums/ticket-status.enum';
import { FormListItemDTO } from '@sb-tools/typescript/models/dto/form-list-item.dto';
import { TicketService } from '../../../core/services/ticket/ticket.service';
import { TicketDetailsPanelService } from '../../../core/services/ticket-details-panel.service';
import { AssignCategoryAndTagsService } from '@form-field-module/common/services/assign-category-and-tags.service';
import { DetailedTicketSource } from '@sb-tools/typescript/enums/detailed-ticket-source.enum';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { InlineSVGComponent } from '../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { FocusDirective } from '../../../../sb-tools/typescript/directives/focus.directive';
import { FormsListComponent } from '../../../../sb-modules/Frontend-form-field-module/forms-list/forms-list.component';
import { LoaderComponent } from '../../../../sb-tools/typescript/components/loader/loader.component';
import { FormsModule } from '@angular/forms';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';

@Component({
    selector: 'app-new-ticket-modal',
    templateUrl: './new-ticket-modal.component.html',
    styleUrls: ['./new-ticket-modal.component.scss'],
    providers: [{ provide: AssignCategoryAndTagsService }],
    imports: [
        TooltipModule,
        InlineSVGComponent,
        FocusDirective,
        FormsListComponent,
        LoaderComponent,
        FormComponent,
        FormsModule,
        BsDropdownModule,
        ModalModule,
        TranslateModule,
    ],
})
export class NewTicketModalComponent implements OnInit, OnDestroy {
    @ViewChild(FormComponent) formComponent: FormComponent;

    areFormsLoading = true;
    form: ClientForm;
    forms: FormListItemDTO[] = [];
    formTypesEnum = FormType;
    initialForm: ClientForm;
    isAssignedToCreator: boolean;
    isCreateAnotherTicket: boolean;
    isFormReady: boolean;
    isSaving: boolean;
    isUploadingFiles: boolean;
    selectedForm = null;
    ticketId: number;

    private unsubscriber = new Subject<void>();

    constructor(
        public modalRef: BsModalRef,
        public formCommonDataService: FormCommonDataService,
        public assignCategoryAndTagsService: AssignCategoryAndTagsService,
        public commonDataService: CommonDataService,
        private changeDetectorRef: ChangeDetectorRef,
        private formFieldsValidationService: FormFieldsValidationService,
        private attachmentService: AttachmentService,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
        private ticketDetailsPanelService: TicketDetailsPanelService,
        private formService: FormService,
        private ticketService: TicketService,
        private messageStorageService: MessageStorageService,
        private formCategoryPreselectionService: FormCategoryPreselectionService,
    ) {}

    ngOnInit() {
        this.formService
            .getAll()
            .pipe(
                finalize(() => {
                    this.areFormsLoading = false;
                }),
            )
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data) => {
                this.forms = data;

                if (this.forms.length === 1) {
                    this.selectForm(this.forms[0]);
                }
            });
    }

    openInNewWindow() {
        const windowWidth = 1000;
        const right = 100;
        const left = window.screen.width - windowWidth - right;
        const windowFeatures = `popup,left=${left},top=100,width=1000,height=800`;

        let link: string;
        let target: string = null;

        if (!this.selectedForm) {
            link = environment.formUrl + this.commonDataService.communityName + '/forms-list';
        } else {
            target = `Form${this.form.id}`;
            link =
                environment.formUrl +
                '/' +
                this.commonDataService.communityName +
                '/' +
                this.form.name +
                '?isInternal=true';
        }

        window.open(link, target, windowFeatures);
    }

    send(isClosedTicket: boolean, isTicketSentAsLink: boolean) {
        this.formService.isSubmitted = true;
        this.isSaving = true;

        if (isClosedTicket && this.formFieldsValidationService.hasMissingRequiredForTeamFields(this.form.fields)) {
            this.notificationsService.error(
                this.translateService.instant('Error!'),
                this.translateService.instant('Required for Agents fields must be filled to close the ticket'),
            );
            this.isSaving = false;
            return;
        }

        if (
            this.formComponent.ticketForm.valid &&
            this.formFieldsValidationService.isRequiredValuesFilled(this.form.fields)
        ) {
            this.sendTicket(isClosedTicket, isTicketSentAsLink);
            this.messageStorageService.clearDraft(false);
        } else {
            setTimeout(() => {
                this.formFieldsValidationService.scrollToInvalidField('#newTicketForm', '#modalContainer');
                this.isSaving = false;
            }, 100);
        }
    }

    selectForm(form: FormListItemDTO): void {
        this.selectedForm = form;

        this.formCommonDataService.formName = this.selectedForm.name;
        this.formCommonDataService.community = this.commonDataService.communityName;

        (this.formCommonDataService.customerSettings as any) = {};
        this.formCommonDataService.customerSettings.isGAEnabled = this.commonDataService.customerSettings.isGAEnabled;

        this.formService
            .get(this.formCommonDataService.formName)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data) => {
                this.initialForm = structuredClone(data);
                this.processForm(data);
                this.isFormReady = true;
            });

        this.isUploadingFiles = false;

        this.attachmentService.isUploadingFilesFinished
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((isFinished: boolean) => {
                this.isUploadingFiles = !isFinished;
            });
    }

    resetForm(): void {
        document.getElementById('modalContainer').scrollTop = 0;
        this.selectedForm = null;
        this.isFormReady = false;
        this.formService.isSubmitted = false;
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }

    private async sendTicket(isCloseTicket: boolean, isTicketSentAsLink: boolean) {
        const ticketModel = new AddTicketModel(
            this.form,
            isCloseTicket ? TicketStatus.completed : null,
            null,
            isTicketSentAsLink,
        );

        ticketModel.isAssignedToCreator = this.isAssignedToCreator;
        ticketModel.ticketSource = DetailedTicketSource.formInternal;

        if (ticketModel.message) {
            ticketModel.message = ticketModel.message.replaceAll('<div></div>', '<div><br></div>');
        }

        this.assignCategoryAndTagsService
            .assignCategoryAndTags(ticketModel)
            .pipe(
                takeUntil(this.unsubscriber),
                finalize(() => {
                    this.isSaving = false;
                }),
            )
            .subscribe((data) => {
                this.ticketService
                    .create(data)
                    .pipe(
                        takeUntil(this.unsubscriber),
                        finalize(() => {
                            this.isSaving = false;
                        }),
                    )
                    .subscribe((ticketId: number) => {
                        this.ticketId = ticketId;
                        this.formCategoryPreselectionService.saveSelectedCategoryIfNecessary(
                            this.form.fields,
                            ticketModel,
                        );
                        this.onTicketCreated();
                    });
            });
    }

    private processForm(data: ClientForm) {
        this.form = data;
        this.formCommonDataService.form = data;

        this.messageStorageService.formId = this.form.id;
        this.formService.isHiddenCategoriesAllowed = this.form.isHiddenCategoriesAllowed;
        this.formService.activeSentimentValue = '';
    }

    private onTicketCreated() {
        if (this.isCreateAnotherTicket) {
            this.isFormReady = false;
            this.formService.isSubmitted = false;
            this.form = structuredClone(this.initialForm);
            this.processForm(this.form);
            this.changeDetectorRef.detectChanges();
            this.isFormReady = true;
        } else {
            this.modalRef.hide();
        }
        this.ticketDetailsPanelService.open(this.ticketId, false, true);
        this.notificationsService.success(
            this.translateService.instant('Success!'),
            this.translateService.instant('New ticket was successfully created.'),
        );
    }

    ngOnDestroy() {
        this.formCommonDataService.form = null;
        this.formService.isSubmitted = false;
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
